import { CustomFlowbiteTheme, TextInput } from "flowbite-react";
import { Button } from "@components";
import {
  cn,
  dropdownInput,
  dropdownResults,
  dropdownResultsDivided,
} from "@design-system";
import {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  type FC,
  Children,
} from "react";
import { HiSearch, HiOutlinePlus } from "react-icons/hi";

export interface DropdownSearchProp {
  placeholder: string;
  value?: string;
  children?: React.ReactNode;
  showResults?: boolean;
  childrenAbsolute?: boolean;
  plain?: boolean;
  divide?: boolean;
  buttonText?: string;
  inputChanged?: (input: string) => void;
  buttonClick?: () => void;
  clickOutside?: (show: boolean) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  paginate?: boolean;
  pageSize?: number;
}

const selectedTheme: CustomFlowbiteTheme = {
  textInput: {
    field: {
      input: {
        colors: {
          primary: dropdownInput,
        },
      },
    },
  },
};

export const DropdownSearch: FC<DropdownSearchProp> = (props) => {
  const [value, setValue] = useState(props.value || "");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const defaultPageSize = props.pageSize || 15;

  useEffect(() => {
    setPage(1);
  }, [props.value, props.children]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        props.clickOutside && props.clickOutside(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props, props.clickOutside]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    props.inputChanged && props.inputChanged(event.target.value);
  };

  useEffect(() => {
    setValue(props.value || "");
  }, [props.value]);

  const childrenArray = Children.toArray(props.children);
  const displayedChildren = props.paginate
    ? childrenArray.slice(0, page * defaultPageSize)
    : childrenArray;

  const loadMore = () => {
    setPage((prev) => prev + 1);
  };

  const resultsPanelClass = cn(
    dropdownResults,
    props.divide !== false && dropdownResultsDivided,
    props.childrenAbsolute && "bg-white absolute",
    !props.plain && "shadow-xl rounded-xl p-4",
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex gap-4 items-center align-center">
        <div className="grow">
          <TextInput
            theme={selectedTheme.textInput}
            color="primary"
            autoComplete="off"
            value={value || ""}
            icon={HiSearch}
            id="search"
            name="search"
            placeholder={props.placeholder}
            required
            size={32}
            type="search"
            onChange={handleInputChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
          />
        </div>
        {props.buttonText && (
          <div className="whitespace-nowrap flex items-center justify-start">
            <Button
              variant="secondary"
              onClick={props.buttonClick ? props.buttonClick : () => {}}
              icon={<HiOutlinePlus className="dark:text-white" />}
              className="border-lgb-grey-200 dark:border dark:border-gray-500"
            >
              {props.buttonText}
            </Button>
          </div>
        )}
      </div>

      {props.showResults && (
        <ul className={resultsPanelClass}>
          {displayedChildren}
          {props.paginate &&
            displayedChildren.length < childrenArray.length && (
              <li>
                <p
                  className="pt-4 text-md underline font-normal cursor-pointer hover:text-lgb-primary"
                  onClick={loadMore}
                >
                  View More
                </p>
              </li>
            )}
        </ul>
      )}
    </div>
  );
};
